/**
 * Documents.js — the paper filed against a deal
 * =============================================
 * Usability review of 23 October 2026, item 23: a Documents tab on each deal,
 * and item 3: "a deal should link back to its position, its notices and its
 * documents."
 *
 * What the book holds as documents is the notice register: every conversion,
 * equity-line settlement and warrant exercise notice parsed out of the deal
 * folder or the mailbox, with its figures and whether it has been booked
 * (`GET /api/v1/securities/notices/?deal=<id>`, read-only). Each one links to
 * the file it was read from where that is a Drive file; one found in the mail
 * is confirmed or rejected on Notice Review, which is linked too.
 *
 * This is the register's view of the deal, not the whole deal folder: term
 * sheets, subscription agreements and closing sets are in Drive and nowhere
 * on the book, and the tab says so rather than implying the list is all of it
 * (CLAUDE.md 0.47 — "not in the book" is a claim about the search).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import { Badge, Card, EmptyState, LoadingSpinner } from "../../components/ui";
import { ExportCsvButton } from "../../components/PageTools";
import { noticeReviewPath } from "../../components/links";
import { toNumber } from "../../components/format";
import theme from "../../components/theme";
import { day, plainNumber, shares } from "./figures";
import { readerText } from "./text";

const TYPE_LABELS = {
  CONVERSION: "Note conversion",
  ELOC_ADVANCE: "Equity-line advance or settlement",
  WARRANT_EXERCISE: "Warrant exercise",
};

// The register's status, as a reader would put it. APPLIED is booked to the
// positions; the other three are waiting on somebody.
export function noticeStatus(notice) {
  const warnings = notice.parse_warnings || [];
  if (warnings.some((w) => /must not post/i.test(w))) return { text: "not to be booked", variant: "gray" };
  if (warnings.some((w) => /awaiting review/i.test(w))) return { text: "awaiting review", variant: "amber" };
  switch (notice.status) {
    case "APPLIED":
      return { text: "booked", variant: "green" };
    case "CONFIRMED":
      return { text: "confirmed, not booked", variant: "blue" };
    case "NEEDS_REVIEW":
      return { text: "needs review", variant: "amber" };
    default:
      return { text: "read, not reviewed", variant: "amber" };
  }
}

// A Drive file id is what the crawler stores; a notice the mail watch found
// carries "gmail:…" and has no file of its own to open.
export function documentLink(notice) {
  const id = (notice.source_id || "").trim();
  if (!id || id.includes(":")) return null;
  return `https://drive.google.com/file/d/${encodeURIComponent(id)}/view`;
}

export function noticeFigures(notice) {
  const bits = [];
  // On an equity-line notice the register's principal field holds the
  // advance's purchase amount; on a conversion it is the face converted.
  const advance = notice.notice_type === "ELOC_ADVANCE";
  if (toNumber(notice.principal_converted)) {
    bits.push(`${plainNumber(notice.principal_converted)}${advance ? " advanced" : " of principal"}`);
  }
  if (toNumber(notice.interest_converted)) bits.push(`${plainNumber(notice.interest_converted)} of interest`);
  const into = toNumber(notice.shares_issued) ? `${shares(notice.shares_issued)} shares` : "";
  const at = toNumber(notice.conversion_price) ? ` at ${plainNumber(notice.conversion_price, 4)}` : "";
  const head = bits.join(" + ");
  const text = [head, into].filter(Boolean).join(" → ");
  return text ? `${text}${at}${notice.currency ? ` (${notice.currency})` : ""}` : "";
}

const noticeCache = new Map();

async function fetchNotices(dealId) {
  // The register pages at fifty; an equity line can carry more than that.
  const out = [];
  for (let page = 1; page <= 20; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await api.get("/securities/notices/", { params: { deal: dealId, page } });
    const body = res.data;
    if (Array.isArray(body)) return body;
    out.push(...(body?.results || []));
    if (!body?.next) break;
  }
  return out;
}

export function useDealNotices(dealId) {
  const [state, setState] = useState(() => ({ data: noticeCache.get(dealId) || null, error: "" }));
  useEffect(() => {
    let live = true;
    const cached = noticeCache.get(dealId);
    setState({ data: cached || null, error: "" });
    if (cached) return undefined;
    fetchNotices(dealId)
      .then((rows) => {
        noticeCache.set(dealId, rows);
        if (live) setState({ data: rows, error: "" });
      })
      .catch((err) => live && setState({ data: null, error: err.response?.data?.detail || err.message || "Failed to load" }));
    return () => {
      live = false;
    };
  }, [dealId]);
  return state;
}

export function clearNoticeCache() {
  noticeCache.clear();
}

function NoticeRow({ notice, open, onToggle }) {
  const status = noticeStatus(notice);
  const link = documentLink(notice);
  const figures = noticeFigures(notice);
  const notes = (notice.parse_warnings || []).map((w) => readerText(w)).filter(Boolean);
  return (
    <>
      <tr className={`dr-act-row${open ? " is-open" : ""}`} onClick={onToggle}>
        <td className="dr-act-date">{day(notice.notice_date)}</td>
        <td className="dr-act-what" title={notice.source_title || ""}>
          <span style={{ color: theme.text }}>{TYPE_LABELS[notice.notice_type] || notice.notice_type}</span>
          {figures && <span style={{ color: theme.textSecondary }}> · {figures}</span>}
        </td>
        <td style={{ textAlign: "right" }}>
          <Badge text={status.text} variant={status.variant} />
        </td>
        <td style={{ textAlign: "right" }}>
          <button
            type="button"
            className="dr-act-toggle"
            aria-expanded={open}
            aria-label={open ? "Hide the notice's details" : "Show the notice's details"}
            onClick={(ev) => {
              ev.stopPropagation();
              onToggle();
            }}
          >
            {open ? "▾" : "▸"}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="dr-act-detail">
          <td />
          <td colSpan={3}>
            <div style={{ fontSize: 12, color: theme.textSecondary }}>
              {notice.source_title || "No file name recorded"}
              {link && (
                <>
                  {" · "}
                  <a href={link} target="_blank" rel="noreferrer" className="dr-link" onClick={(ev) => ev.stopPropagation()}>
                    Open the document ↗
                  </a>
                </>
              )}
            </div>
            {notice.issuer_name && (
              <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
                {notice.issuer_name}
              </div>
            )}
            {notes.map((n, i) => (
              <div key={i} style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
                {n}
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

export function DealDocuments({ deal }) {
  const { data, error } = useDealNotices(deal.deal.id);
  const [open, setOpen] = useState({});
  const rows = useMemo(
    () => [...(data || [])].sort((a, b) => String(a.notice_date || "").localeCompare(String(b.notice_date || ""))),
    [data]
  );
  const waiting = rows.filter((n) => ["awaiting review", "needs review", "read, not reviewed"].includes(noticeStatus(n).text)).length;
  const csvColumns = [
    { label: "Date", value: (n) => n.notice_date || "" },
    { label: "Type", value: (n) => TYPE_LABELS[n.notice_type] || n.notice_type },
    { label: "Document", value: (n) => n.source_title || "" },
    { label: "Principal", value: (n) => toNumber(n.principal_converted) },
    { label: "Interest", value: (n) => toNumber(n.interest_converted) },
    { label: "Shares", value: (n) => toNumber(n.shares_issued) },
    { label: "Price", value: (n) => toNumber(n.conversion_price) },
    { label: "Currency", value: (n) => n.currency || "" },
    { label: "Status", value: (n) => noticeStatus(n).text },
    { label: "Link", value: (n) => documentLink(n) || "" },
  ];

  return (
    <Card
      title="Notices and documents"
      subtitle={
        data
          ? `${rows.length} ${rows.length === 1 ? "notice" : "notices"} on the register for this deal${
              waiting ? `, ${waiting} waiting on review` : ""
            }`
          : "The notice register for this deal"
      }
      action={<ExportCsvButton filename={`deal-notices-${deal.deal.name}`} columns={csvColumns} rows={rows} />}
    >
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>
        Conversion, equity-line and warrant notices read from the deal folder and the mail. Term
        sheets, agreements and closing sets are in the deal's Drive folder and are not listed here.{" "}
        <Link to={noticeReviewPath()} className="dr-link">
          Open Notice Review
        </Link>{" "}
        to confirm or reject a notice.
      </div>
      {error ? (
        <EmptyState message={`The notice register could not be read: ${error}`} icon="⚠" />
      ) : !data ? (
        <LoadingSpinner message="Reading the notice register…" />
      ) : rows.length === 0 ? (
        <EmptyState message="No notice on the register is filed against this deal." icon="◌" />
      ) : (
        <div className="dr-act-scroll">
          <table className="dr-act">
            <colgroup>
              <col style={{ width: 96 }} />
              <col />
              <col style={{ width: 170 }} />
              <col style={{ width: 28 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Notice</th>
                <th style={{ textAlign: "right" }}>Status</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <NoticeRow key={n.id} notice={n} open={!!open[n.id]} onToggle={() => setOpen((o) => ({ ...o, [n.id]: !o[n.id] }))} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
